import { ButtonLink } from "~/components/ui/button";
import { shouldShowCoachCTAs } from "~/lib/coach-cta-utils";

type Props = {
  coach: {
    username: string;
    firstName: string;
    hasBookingCalendar?: boolean;
    profile: {
      ctaButtonText: string | null;
      contactMode: "booking" | "messaging" | null;
      isPreGeneratedProfile: boolean;
    };
  };
};

/**
 * Closing band on a coach profile. Bookends the hero with the same gradient +
 * purple accent so the page reads as a single piece. Centered stack so the CTA
 * sits directly under the headline and doesn't get lost like a split layout
 * does against the dark site footer.
 */
export function CoachClosingCta({ coach }: Props) {
  // The entire band exists to funnel into contact/booking, so hide it outright
  // for pre-generated (unclaimed) profiles — the claim banner is their CTA.
  if (!shouldShowCoachCTAs(coach)) return null;

  const ctaLabel =
    coach.profile.ctaButtonText?.trim() || "Start a conversation";
  const href =
    coach.profile.contactMode === "booking"
      ? `/${coach.username}/book`
      : `/${coach.username}/contact`;

  return (
    <section className="relative overflow-hidden border-t border-gray-200 bg-linear-to-b from-white via-purple-50/60 to-purple-100/40">
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 left-1/2 size-[520px] -translate-x-1/2 rounded-full bg-purple-200/40 blur-[120px]"
      />
      <div className="relative z-10 mx-auto max-w-3xl px-4 py-20 text-center md:py-24 xl:px-0">
        <div className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
          <span aria-hidden className="size-1.5 rounded-full bg-purple-700" />
          Ready when you are
        </div>
        <h2 className="mt-4 font-heading text-3xl leading-tight font-medium tracking-tight text-gray-900 md:text-5xl">
          Curious if {coach.firstName} could be your coach?
        </h2>
        <div className="mt-8 flex justify-center">
          <ButtonLink href={href} variant="fancy" size="lg">
            {ctaLabel}
          </ButtonLink>
        </div>
      </div>
    </section>
  );
}
